import { useEffect, useMemo, useCallback, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import cn from 'classnames'
import { boxOnlineLink } from '../../constants/common'
import { useRouteChange } from '../../helpers/helpers'
import { PREVIEW_SYNC_ROW_ATTR } from '../../hooks/useBoxPreview'
import { BOX_VIEWER_MODE } from '../../redux/box/actions'
import EmptyData from '../Common/Empty'
import Icon from '../Common/Icon'
import IconButton from '../Common/IconButton'
import { ExternalLink } from '../Common/Icons/Icons'
import styles from './BoxViewer.module.scss'
import { setBoxViewerModalOpen } from './boxViewerModalStore'
import { PropsFromRedux } from './index'

export type IProps = PropsFromRedux

const BoxViewer = ({
  boxViewLink,
  boxViewerMode,
  boxViewerFileId,
  boxViewerFailureMessage,
  setBoxViewerLink,
  setBoxViewerMode,
  isBoxViewerExpanded,
  isAdminLoggedAsClient,
  isExternalUser,
}: IProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const { pathname } = useLocation()
  const drawerRef = useRef<HTMLDivElement>(null)
  const isModal = boxViewerMode === BOX_VIEWER_MODE.MODAL
  // Preserve the current mode when clearing the link. Otherwise the default
  // `mode = 'drawer'` flips us out of modal mode mid-close, and React
  // reconciles the modal `<div>` into the drawer `<div>` at the same root
  // position — which animates `right: 0 → -100%` via the drawer's
  // `transition: right 0.3s` and looks like a phantom drawer collapse.
  const onCancel = useCallback(() => {
    if (boxViewLink) {
      setBoxViewerLink('', boxViewerMode)
    }
  }, [boxViewLink, boxViewerMode, setBoxViewerLink])

  const handleExpand = useCallback(() => {
    setBoxViewerMode(BOX_VIEWER_MODE.MODAL)
  }, [setBoxViewerMode])

  const handleCollapse = useCallback(() => {
    setBoxViewerMode(BOX_VIEWER_MODE.DRAWER)
  }, [setBoxViewerMode])

  const isModalShown = isModal && !!boxViewLink
  useEffect(() => {
    setBoxViewerModalOpen(isModalShown)
    return () => setBoxViewerModalOpen(false)
  }, [isModalShown])

  const isBoxViewerLinkReal = useMemo(() => {
    return boxViewLink.includes('https://')
  }, [boxViewLink])

  const isBoxSignLink = useMemo(() => {
    return boxViewLink.includes('/sign/')
  }, [boxViewLink])

  useRouteChange(() => {
    if (pathname.includes('/bbc') || pathname.includes('/operations')) {
      return
    }
    onCancel()
  }, [pathname])

  useEffect(() => {
    if (boxViewLink) {
      setIsLoading(true)
    }
  }, [boxViewLink])

  // ESC closes in both modes — matches MUI Modal convention so users don't have
  // to reach for the close button. Bail when a dialog is stacked on top of us
  // (the account-activity create/edit/delete modals open over the drawer):
  // Escape belongs to the topmost surface, and closing the viewer underneath
  // would leave the user staring at a dialog they didn't mean to keep.
  //
  // `:not(.MuiPopover-root)` is load-bearing. MUI builds Popover — and so Menu,
  // and so the Select behind our shared SelectField — on top of Modal, so every
  // open dropdown carries `MuiModal-root` too. Matching those would let any
  // stray open filter dropdown silently swallow Escape, on exactly the
  // Account Activity and Fundings screens this is meant to serve. A real dialog
  // is a Modal that is *not* a popover; we can't narrow to `.MuiDialog-root`
  // instead, because the shared Modal wraps MUI's `Modal`, not its `Dialog`.
  //
  // The centered viewer is the topmost surface, so in that mode Escape is caught on the way
  // down and stops there: a dialog the file was opened from stays open underneath.
  useEffect(() => {
    if (!boxViewLink) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (!isModal && document.querySelector('.MuiModal-root:not(.MuiPopover-root)')) return
      if (isModal) e.stopPropagation()
      onCancel()
    }
    window.addEventListener('keydown', onKey, isModal)
    return () => window.removeEventListener('keydown', onKey, isModal)
  }, [isModal, boxViewLink, onCancel])

  // Click-outside closes the drawer. Modal mode gets this from its backdrop.
  //
  // Registered on `click` in the **capture** phase. React's handlers are
  // attached at the app root, i.e. *below* document, so a bubble-phase listener
  // here would run after a table row's onClick and close the panel that row had
  // just swapped to a new document. Capturing puts us first — which is also why
  // rows that retarget the preview have to be excluded outright below rather
  // than left to close-then-reopen: our close flushes a re-render before their
  // handler ever runs, so they'd see a shut panel and do nothing.
  //
  // The effect only attaches once the drawer is open, and effects run after the
  // event that opened it has finished dispatching — so the opening click can
  // never reach this listener and immediately undo itself. Clicks inside the
  // `<iframe>` don't propagate to `document` either, so paging through a file
  // doesn't dismiss it.
  useEffect(() => {
    if (isModal || !boxViewLink) return
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node | null
      // A node already detached by this click (a row that re-rendered away)
      // reads as "outside" purely because it is no longer in the document.
      if (!target || !document.contains(target)) return
      if (drawerRef.current?.contains(target)) return
      // Related to the Escape guard but deliberately broader, and target-scoped
      // rather than global: anything modal-ish stacked over the drawer renders
      // in a portal outside it, and clicking *into* it — a dialog, or an open
      // dropdown's option list — is interacting with that surface, not clicking
      // the page outside the panel. Popovers are included here for that reason,
      // where Escape above must exclude them.
      const element = target instanceof Element ? target : target.parentElement
      if (element?.closest('.MuiModal-root')) return
      // A row that retargets the preview owns this click end to end: it either
      // swaps the panel to its own document or closes it when it has none. We
      // must not close first — this listener is ahead of the row's React
      // handler, and the close flushes a re-render that makes the row's
      // `syncPreviewToRow` see a shut panel and no-op, so the swap never
      // happens and the click just dismisses. See PREVIEW_SYNC_ROW_ATTR.
      if (element?.closest(`[${PREVIEW_SYNC_ROW_ATTR}]`)) return
      onCancel()
    }
    document.addEventListener('click', onClickOutside, true)
    return () => document.removeEventListener('click', onClickOutside, true)
  }, [isModal, boxViewLink, onCancel])

  const isFailed = useMemo(() => {
    return boxViewLink === 'failed'
  }, [boxViewLink])

  const handleIframeLoad = useCallback(() => {
    setIsLoading(false)
  }, [])

  // Header strip keeps our close button out of the iframe's native Box toolbar
  // (download, expand, …) — those live top-right inside the iframe and overlap
  // our X if we place it there. Shared by both modes so the drawer offers the
  // same close affordance as the pop-up.
  const header = (
    <div className={styles.modalHeader}>
      {boxViewerFileId && !isExternalUser && (
        <ExternalLink
          link={boxOnlineLink('file', boxViewerFileId)}
          title="Open in Box"
          size="small"
          data-cy={isModal ? 'boxviewer-modal-open-in-box' : 'boxviewer-open-in-box'}
        />
      )}
      {!isModal && (
        <IconButton
          title="Expand preview"
          data-cy="boxviewer-expand-button"
          onClick={handleExpand}
          size="small"
        >
          <Icon name="expand" size="sm" />
        </IconButton>
      )}
      {/* Only a preview expanded from the drawer offers to go back there: a modal opened
          on purpose (AIM, or a link inside a dialog) would dock under what opened it. */}
      {isModal && isBoxViewerExpanded && (
        <IconButton
          title="Collapse preview to the side"
          data-cy="boxviewer-collapse-button"
          onClick={handleCollapse}
          size="small"
        >
          <Icon name="shrink" size="sm" />
        </IconButton>
      )}
      <IconButton
        title="Close"
        data-cy={isModal ? 'boxviewer-modal-close-button' : 'boxviewer-close-button'}
        onClick={onCancel}
        size="small"
      >
        <Icon name="x" size="sm" />
      </IconButton>
    </div>
  )

  const body = (
    <div className={cn(styles.modalBody)}>
      {isBoxViewerLinkReal ? (
        <>
          {isLoading && (
            <div className={styles.loaderWrapper}>
              <CircularProgress color="primary" size={100} thickness={1} />
            </div>
          )}

          <iframe
            title="Dwight Funding"
            src={boxViewLink}
            width="100%"
            height="100%"
            allowFullScreen
            className={cn(styles.iframe, {
              [styles.boxSignIframe]: isBoxSignLink,
              [styles.iframeHidden]: isLoading,
            })}
            onLoad={handleIframeLoad}
          />
        </>
      ) : isFailed ? (
        <div className={styles.loaderWrapper}>
          <EmptyData
            message={boxViewerFailureMessage ?? 'Failed to load the file. Please try again later.'}
          />
        </div>
      ) : (
        <div className={styles.loaderWrapper}>
          <CircularProgress color="primary" size={100} thickness={1} />
        </div>
      )}
    </div>
  )

  if (isModal && !boxViewLink) return null

  // One panel element in both modes, with the backdrop as a sibling rather than a
  // parent, so switching modes restyles the panel instead of remounting it. A remount
  // would reload the iframe, and Box embed links expire minutes after they are issued.
  return (
    <>
      {isModal && <div className={styles.modalBackdrop} onClick={onCancel} />}
      <div
        ref={drawerRef}
        role={isModal ? 'dialog' : undefined}
        aria-modal={isModal || undefined}
        // The closed drawer stays mounted off-screen; keep its buttons out of the tab order.
        inert={!boxViewLink}
        className={
          isModal
            ? styles.modalContainer
            : cn(styles.customDrawer, {
                [styles.expanded]: !!boxViewLink,
                [styles.isAdminLoggedAsClient]: isAdminLoggedAsClient,
                [styles.boxSignDrawer]: isBoxSignLink,
              })
        }
      >
        {header}
        {body}
      </div>
    </>
  )
}

export default BoxViewer
